import classNames from 'classnames'
import { motion } from 'framer-motion'

import { CopyWithText, ImageComponent, ValueWithText } from '@apps-common/components/common'
import { VertSpacerMd, VertSpacerXS } from '@apps-common/components/layout'
import { ConfirmModal, ModalButton } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { GRID_CARD_VARIANTS } from '@apps-common/config/motion-variants'
import { UNIT_STATUS_ACTIVE } from '@apps-common/config/unit-filter'
import { valueStringCheck } from '@apps-common/helpers/value-string-check'
import { useNavigation } from '@apps-common/hooks/useNavigation'
import { usePortalFirebaseUser } from '@apps-common/hooks/usePortalFirebaseUser'
import { IMyMonitoringAccountUnitItemModel } from '@apps-common/types/portal/my-monitoring'
import { ROUTE_MY_MONITORING_ACCOUNT_DETAILS, ROUTE_UNIT_MANAGE } from '@apps-radionet-portal-root/src/config/private-routes'

import { ButtonRoundedWithIcon, ButtonRoundSm } from '@apps-common/components/elements'
import { MyMonitoringUnitAddModal } from './MyMonitoringUnitAddModal'

interface IProps {
  disabled?: boolean
  unit: IMyMonitoringAccountUnitItemModel
  updateCallback: (unit: IMyMonitoringAccountUnitItemModel) => void
  removeCallback: (unitId: string) => void
}

export const MyMonitoringAccountUnitCard: React.FC<IProps> = ({ disabled, unit, updateCallback, removeCallback }) => {
  const { unitId, accountNo, monitoringCentre, bureau, productImageUrl, siteName, userNumber, userPartition } = unit

  const { navigateForward } = useNavigation()
  const fbUser = usePortalFirebaseUser()

  return (
    <motion.div
      variants={GRID_CARD_VARIANTS}
      className={classNames('w-full rounded-[34px] p-4 overflow-hidden bg-surface text-text border border-border')}
    >
      <div className='flex items-stretch justify-between gap-[10px] h-full'>
        <div className='grow'>
          <div className='flex items-center justify-start'>
            <h4 className='jost-light text-[0.85rem] text-ellipsis whitespace-nowrap overflow-hidden flex items-center justify-start gap-[5px] text-text-muted'>
              {fbUser?.isMonitoringCentre ? bureau : monitoringCentre}
            </h4>
          </div>
          <div className='flex items-center justify-between gap-[10px]'>
            <div className='flex items-center justify-start gap-[5px]'>
              <div className='px-4 py-2 h-[20px] rounded-[15px] flex items-center justify-start gap-[10px] min-w-0 bg-surface-2'>
                <commonIcons.processor className='text-text-muted' />
                <h5 className='jost-light text-[0.85rem] text-text uppercase'>{valueStringCheck(accountNo)}</h5>
              </div>
            </div>
            <div className='w-[45px] h-[45px] rounded-[23px] flex items-center justify-center overflow-hidden bg-surface-2'>
              <ImageComponent
                src={productImageUrl}
                alt={`Product ${accountNo}`}
                imageClasses='max-w-[30px]'
                errorIconClasses='text-[1.2rem]'
              />
            </div>
          </div>
          <VertSpacerXS />

          <div className='flex flex-col items-start justify-start gap-[5px]'>
            <CopyWithText textValue={siteName} noTextValue='<No site info>' value={siteName} />
            <div className='flex flex-col gap-[5px]'>
              <ValueWithText textValue='User#' value={userNumber} />
              <ValueWithText textValue='Partition#' value={userPartition} />
            </div>
          </div>

          <VertSpacerMd />
          <div className='flex items-center justify-start gap-[6px]'>
            <ModalButton
              component={MyMonitoringUnitAddModal}
              disabled={disabled}
              componentProps={unit}
              backdropDismiss
              confirmCallback={(data) => updateCallback(data)}
            >
              <ButtonRoundSm type='div' className='action-neutral'>
                <commonIcons.edit />
              </ButtonRoundSm>
            </ModalButton>
            <ModalButton
              component={ConfirmModal}
              disabled={disabled}
              componentProps={{
                text: 'Remove',
                description: 'Confirm you would like to remove this device.'
              }}
              backdropDismiss
              confirmCallback={() => removeCallback(unitId)}
            >
              <ButtonRoundSm type='div' className='action-error'>
                <commonIcons.remove />
              </ButtonRoundSm>
            </ModalButton>

            <ButtonRoundedWithIcon
              type='button'
              text='View Device'
              icon={<commonIcons.arrowAngled />}
              className='action-brand !grow'
              iconContainerClassName='bg-brand text-on-brand'
              disabled={disabled}
              callback={(e) => {
                e?.stopPropagation()
                navigateForward({
                  routeUrl: ROUTE_UNIT_MANAGE.buildPath(UNIT_STATUS_ACTIVE.value, unitId),
                  route: ROUTE_MY_MONITORING_ACCOUNT_DETAILS
                })
              }}
            />

            {/* <button
              type='button'
              aria-label='View device'
              className=' !bg-secondary-content/5 text-secondary-content flex items-center justify-center gap-[5px] jost-light text-[0.8rem] w-[34px] h-[34px] rounded-full'
              disabled={disabled}
              onClick={(e) => {
                e?.stopPropagation()
                navigateForward({
                  routeUrl: ROUTE_UNIT_MANAGE.buildPath(UNIT_STATUS_ACTIVE.value, unitId),
                  route: ROUTE_MY_MONITORING_ACCOUNT_DETAILS
                })
              }}
            >
              <commonIcons.arrowAngled className='text-[0.9rem]' />
            </button> */}
          </div>
        </div>
      </div>
    </motion.div>
  )
}
